import { useQuery } from '@tanstack/react-query';
import { joinStatsApi, type HealthDay, type HealthStatus, type JoinHealth } from '../../api/joinstats.js';

/**
 * "지금 신규가입에 이상이 있는가" 에만 답하는 배너.
 *
 * 요일별 표·차트가 조사용이라면 이건 감시용이다. 운영자가 페이지를 열자마자
 * 스크롤 없이 답을 봐야 하므로 화면 맨 위에 둔다. 판정 근거(기대값·평소 범위·
 * 표본 수)를 늘 함께 적는다 - 근거 없는 빨간불은 운영자가 믿지 않는다.
 */

const TONE: Record<HealthStatus, { badge: string; metric: string; dot: string }> = {
  ok:      { badge: 'badge--ok',     metric: 'ok',     dot: 'var(--ok)' },
  warn:    { badge: 'badge--warn',   metric: 'warn',   dot: 'var(--warn)' },
  danger:  { badge: 'badge--danger', metric: 'danger', dot: 'var(--danger)' },
  spike:   { badge: 'badge--sky',    metric: 'warn',   dot: 'skyblue' },
  low:     { badge: 'badge--info',   metric: '',       dot: 'var(--accent)' },
  pending: { badge: 'badge--grade',  metric: '',       dot: 'var(--text-muted)' },
};

const fmtNum = (v: number | null | undefined) => (Number(v) || 0).toLocaleString('ko-KR');

function fmtPct(v: number | null | undefined): string {
  if (v === null || v === undefined) return '-';
  return `${Number(v).toFixed(1).replace(/\.0$/, '')}%`;
}

/** 기준 대비 증감을 부호와 함께. 100% 가 '변화 없음' 이다. */
function fmtDelta(ratio: number | null): string {
  if (ratio === null) return '-';
  const d = ratio - 100;
  return `${d > 0 ? '+' : ''}${d.toFixed(1).replace(/\.0$/, '')}%`;
}

export function JoinHealthBanner() {
  const q = useQuery({
    queryKey: ['join-health'],
    queryFn: () => joinStatsApi.health(),
    // 원본 자료가 매시 01분 배치로 채워진다 - 그보다 촘촘히 볼 이유가 없다.
    refetchInterval: 5 * 60_000,
  });

  if (q.isPending) return <div className="panel join-health join-health--idle">상태 확인 중…</div>;
  if (q.isError) {
    return (
      <div className="panel join-health join-health--idle">
        상태를 불러오지 못했습니다.{' '}
        <button type="button" className="btn btn--ghost" onClick={() => void q.refetch()}>
          다시 시도
        </button>
      </div>
    );
  }

  const h: JoinHealth = q.data;
  const tone = TONE[h.status];
  const abnormal = h.status === 'danger' || h.status === 'warn' || h.status === 'spike';

  return (
    <div className={`panel join-health join-health--${h.status}`}>
      <div className="join-health__head">
        <span className="join-health__dot" style={{ background: tone.dot }} />
        <span className={`badge ${tone.badge} join-health__badge`}>{h.statusLabel}</span>
        <span className="join-health__reason">{h.reason}</span>
        <span className="join-health__asof">
          {h.asOf.date} ({h.asOf.dowLabel}) {h.asOf.hourLabel} 기준
          {h.asOf.complete ? ' · 하루 마감' : ''}
        </span>
      </div>

      <div className="join-metrics join-health__metrics">
        <Cell
          tone={tone.metric}
          label={`오늘 신규가입 (${h.asOf.hourLabel}까지)`}
          value={fmtNum(h.today.joins)}
          sub={`유입 ${fmtNum(h.today.inflow)} · 전환 ${fmtPct(h.today.conversion)}`}
        />
        <Cell
          label={`최근 ${h.baseline.weeks}주 ${h.asOf.dowLabel}요일 기준`}
          value={fmtNum(h.baseline.expected)}
          sub={`표본 ${fmtNum(h.baseline.sampleCount)}주 중앙값`}
        />
        <Cell
          label="평소 범위"
          value={`${fmtNum(h.baseline.low)} ~ ${fmtNum(h.baseline.high)}`}
          sub="기대값 ± 1.5σ"
        />
        <Cell
          tone={tone.metric}
          label="기대 대비"
          value={fmtPct(h.ratio)}
          sub={h.ratio === null ? '기준 없음' : `${fmtDelta(h.ratio)} 차이`}
        />
        <Cell
          label="전주 동일 요일"
          value={h.lastWeek ? fmtNum(h.lastWeek.joins) : '-'}
          sub={h.lastWeek ? `대비 ${fmtDelta(h.lastWeek.ratio)}` : '전주 자료 없음'}
        />
        <Cell
          tone={h.lastAnomaly ? 'warn' : ''}
          label="최근 이상"
          value={h.lastAnomaly ? `${h.lastAnomaly.date.slice(5)} (${h.lastAnomaly.dowLabel})` : '없음'}
          sub={
            h.lastAnomaly
              ? `${h.lastAnomaly.statusLabel} · ${fmtNum(h.lastAnomaly.joins)}명 (기대 ${fmtNum(h.lastAnomaly.expected)})`
              : `최근 ${h.recent.length}일 이상 없음`
          }
        />
      </div>

      {/* 이상일 때만 채널을 편다 - 평소에는 숫자가 많을수록 눈에 안 들어온다. */}
      {abnormal && h.channels.length > 0 && (
        <div className="join-health__channels">
          <span className="join-health__channels-label">어디가 줄었나</span>
          {h.channels.map((c) => (
            <span
              key={c.key}
              className={`join-health__chip join-health__chip--${c.status}`}
              title={`${c.label} 오늘 ${fmtNum(c.today)}명 / 기대 ${fmtNum(c.expected)}명`}
            >
              {c.label} <b>{fmtNum(c.today)}</b>
              <i>/ {fmtNum(c.expected)}</i>
              <em>{fmtDelta(c.ratio)}</em>
            </span>
          ))}
        </div>
      )}

      <Trend days={h.recent} />
    </div>
  );
}

/** 최근 14일 추이. 막대 높이는 기대 대비 비율이라 요일 효과가 상쇄돼 보인다. */
function Trend({ days }: { days: HealthDay[] }) {
  if (days.length === 0) return null;
  const max = Math.max(120, ...days.map((d) => d.ratio ?? 0));

  return (
    <div className="join-health__trend">
      <span className="join-health__trend-label">최근 {days.length}일 (기대 대비)</span>
      <div className="join-health__spark">
        {days.map((d) => (
          <span
            key={d.date}
            className={`join-health__spark-bar join-health__spark-bar--${d.status}`}
            style={{ height: `${Math.max(4, Math.round(((d.ratio ?? 0) / max) * 100))}%` }}
            title={`${d.date} (${d.dowLabel}) ${d.statusLabel} · ${fmtNum(d.joins)}명 / 기대 ${fmtNum(d.expected)}명 (${fmtPct(d.ratio)})`}
          />
        ))}
      </div>
      {/* 100% 선 - 이 위아래로 갈리는 게 한눈에 보여야 한다. */}
      <span className="join-health__baseline" style={{ bottom: `${Math.round((100 / max) * 100)}%` }} />
    </div>
  );
}

function Cell({ label, value, sub, tone }: { label: string; value: string; sub: string; tone?: string }) {
  return (
    <article className={tone ? `join-metric join-metric--${tone}` : 'join-metric'}>
      <span className="join-metric__label">{label}</span>
      <strong className="join-metric__value">{value}</strong>
      <span className="join-metric__sub">{sub}</span>
    </article>
  );
}
